import { HOST_TAG, TAG_TAG, TEXT_SELECTOR, bgElements, bgUrl, findHeadline, imageSrcOf, images, textBlocks, textOf } from "./dom";
import { Editor } from "./edit";
import { detectLang, type Lang } from "./i18n";
import {
  IPTC_SOURCE,
  articleFingerprint,
  SITE_ARTICLE_FP,
  normalizePath,
  srcFingerprint,
  textFingerprint,
  type Origin,
  type PublicMark,
  type PublicPolicy,
  type PublicSitePayload,
  type StaticBundle,
} from "./shared";
import { Layer, imageBadge, placeBadge, textTag, type Label } from "./ui";

declare const __VERSION__: string;

const DEFAULT_POLICY: PublicPolicy = {
  labelStyle: "eu",
  euTone: "auto",
  showDetails: true,
  textPlacement: "start",
  showImageBadge: true,
  showTextLabel: true,
  hideReviewedText: false,
  showHumanLabel: false,
  badgePosition: "top-right",
  theme: "dark",
  accent: "#8b6cff",
  locale: "auto",
  showTransparencyLink: true,
};

const MANUAL: Record<string, Origin> = {
  "ai-generated": "AI_GENERATED",
  ai: "AI_GENERATED",
  "ai-edited": "AI_EDITED",
  human: "HUMAN",
};

interface Decoration {
  el: Element;
  label: Label;
  mark?: PublicMark;
  badge?: HTMLElement;
  tag?: HTMLElement;
}

export interface App {
  siteId: string | null;
  apiBase: string;
  lang: Lang;
  layer: Layer;
  policy: PublicPolicy;
  marks: PublicMark[];
  decorations: Decoration[];
  pagePath: () => string;
  reload: () => Promise<void>;
  apply: () => void;
  editing: boolean;
}

function boot() {
  const w = window as unknown as { AIMarker?: unknown };
  if (w.AIMarker) return; // loaded twice
  const script = document.currentScript as HTMLScriptElement | null;
  const ds = script?.dataset ?? {};
  const siteId = ds.site || null;
  const apiBase = (ds.api || (script?.src ? new URL(script.src).origin : location.origin)).replace(/\/$/, "");

  // Script-level overrides (useful for demos and static sites without a dashboard site)
  const overrides: Partial<PublicPolicy> = {};
  if (ds.theme === "light" || ds.theme === "dark") overrides.theme = ds.theme;
  if (ds.accent && /^#[0-9a-f]{6}$/i.test(ds.accent)) overrides.accent = ds.accent;
  if (ds.position) overrides.badgePosition = ds.position as PublicPolicy["badgePosition"];
  if (ds.lang === "pl" || ds.lang === "en") overrides.locale = ds.lang;
  if (ds.style === "eu" || ds.style === "aimarker") overrides.labelStyle = ds.style;

  let policy: PublicPolicy = { ...DEFAULT_POLICY, ...overrides };
  let site: PublicSitePayload["site"] | null = null;
  const layer = new Layer(policy);

  const app: App = {
    siteId,
    apiBase,
    lang: detectLang(policy.locale),
    layer,
    policy,
    marks: [],
    decorations: [],
    editing: false,
    pagePath: () => normalizePath(location.pathname),
    reload: load,
    apply,
  };

  // Continuity: last good payload per page (localStorage) and an optional static bundle on the client's server
  const staticUrl = ds.static || null;
  const staticOnly = ds.mode === "static";
  const cacheKey = (path: string) => `aimarker:${siteId}:${path}`;
  const CACHE_TTL = 90 * 86400_000;

  function readCache(path: string): PublicSitePayload | null {
    try {
      const raw = localStorage.getItem(cacheKey(path));
      if (!raw) return null;
      const c = JSON.parse(raw) as { t: number; d: PublicSitePayload };
      return Date.now() - c.t < CACHE_TTL ? c.d : null;
    } catch {
      return null;
    }
  }
  function writeCache(path: string, d: PublicSitePayload) {
    try {
      localStorage.setItem(cacheKey(path), JSON.stringify({ t: Date.now(), d }));
    } catch {
      /* storage full or blocked */
    }
  }

  let bundle: Promise<StaticBundle | null> | null = null;
  async function readStatic(path: string): Promise<PublicSitePayload | null> {
    if (!staticUrl) return null;
    bundle ??= fetch(staticUrl, { credentials: "omit" })
      .then((r) => (r.ok ? (r.json() as Promise<StaticBundle>) : null))
      .then((b) => (b && b.format === "aimarker-static" ? b : null))
      .catch(() => null);
    const b = await bundle;
    return b ? { site: b.site, policy: b.policy, marks: [...(b.pages[path] ?? []), ...b.siteWide] } : null;
  }

  function use(data: PublicSitePayload) {
    site = data.site;
    policy = { ...DEFAULT_POLICY, ...data.policy, ...overrides };
    app.policy = policy;
    app.marks = data.marks;
    app.lang = detectLang(policy.locale);
    layer.setTheme(policy);
  }

  async function load() {
    const path = app.pagePath();
    let shownFromCache = "";
    if (siteId && !app.editing && !staticOnly) {
      // Paint the last known labels immediately, then refresh from the network
      const cached = readCache(path);
      if (cached) {
        use(cached);
        apply();
        shownFromCache = JSON.stringify(cached);
      }
    }
    let data: PublicSitePayload | null = null;
    if (siteId && !staticOnly) {
      try {
        const res = await fetch(`${apiBase}/api/v1/public/sites/${encodeURIComponent(siteId)}/marks?path=${encodeURIComponent(path)}&v=${encodeURIComponent(__VERSION__)}`, {
          credentials: "omit",
          // While labelling, always read fresh data (the public endpoint is cached for readers)
          cache: app.editing ? "no-store" : "default",
        });
        if (res.ok) {
          data = (await res.json()) as PublicSitePayload;
          writeCache(path, data);
        }
      } catch {
        /* network errors must never break the host page */
      }
    }
    // API unreachable or the account is gone: keep showing labels from the cache or the static bundle
    data ??= (siteId && !staticOnly ? readCache(path) : null) ?? (await readStatic(path));
    if (data && JSON.stringify(data) === shownFromCache) return;
    if (data) use(data);
    apply();
  }

  let applying = false;
  function clear() {
    for (const d of app.decorations) {
      d.badge?.remove();
      d.tag?.remove();
      d.el.removeAttribute("data-aimarker-origin");
    }
    app.decorations = [];
  }

  function visible(kind: Label["kind"], label: Label) {
    if (app.editing) return true;
    if (label.origin === "HUMAN" && !policy.showHumanLabel) return false;
    if (kind === "IMAGE") return policy.showImageBadge;
    if (!policy.showTextLabel) return false;
    return !(policy.hideReviewedText && label.reviewedBy);
  }

  function apply() {
    applying = true;
    try {
      clear();
      const found = new Map<Element, Decoration>();

      const texts = new Map<string, Element[]>();
      for (const el of textBlocks()) {
        const fp = textFingerprint(textOf(el));
        texts.set(fp, [...(texts.get(fp) ?? []), el]);
      }
      const imgs = new Map<string, Element[]>();
      for (const img of images()) {
        const fp = srcFingerprint(imageSrcOf(img)!, location.href);
        imgs.set(fp, [...(imgs.get(fp) ?? []), img]);
      }
      // CSS background images are only scanned when they can matter (it costs a computed-style pass)
      if (app.editing || app.marks.some((m) => m.kind === "IMAGE")) {
        for (const el of bgElements()) {
          const fp = srcFingerprint(bgUrl(el)!, location.href);
          imgs.set(fp, [...(imgs.get(fp) ?? []), el]);
        }
      }

      // Whole-article mark for this page (or manual data-aimarker-article attribute)
      let article: Decoration | null = null;
      const articleMark =
        app.marks.find((m) => m.kind === "ARTICLE" && m.fingerprint === articleFingerprint(location.pathname)) ??
        app.marks.find((m) => m.kind === "ARTICLE" && m.fingerprint === SITE_ARTICLE_FP && m.pagePath === "*");
      const manualArticle = document.querySelector<HTMLElement>("[data-aimarker-article]");
      const headline = articleMark || manualArticle ? findHeadline(manualArticle ?? document) : null;
      if (headline && articleMark) {
        article = { el: headline, mark: articleMark, label: { kind: "ARTICLE", origin: articleMark.origin, tool: articleMark.tool, reviewedBy: articleMark.reviewedBy, reviewedAt: articleMark.reviewedAt, createdAt: articleMark.createdAt } };
      } else if (headline && manualArticle) {
        const origin = MANUAL[(manualArticle.dataset.aimarkerArticle || "").toLowerCase()];
        if (origin) article = { el: headline, label: { kind: "ARTICLE", origin, tool: manualArticle.dataset.aimarkerTool || null, reviewedBy: manualArticle.dataset.aimarkerReviewedBy || null, reviewedAt: null, createdAt: manualArticle.dataset.aimarkerDate || null } };
      }
      const articleShown = !!article && article.label.origin !== "HUMAN" && visible("ARTICLE", article.label);

      for (const m of app.marks) {
        if (m.kind === "ARTICLE") continue;
        const els = (m.kind === "TEXT" ? texts : imgs).get(m.fingerprint) ?? [];
        for (const el of els) {
          found.set(el, {
            el,
            mark: m,
            label: { kind: m.kind, origin: m.origin, tool: m.tool, reviewedBy: m.reviewedBy, reviewedAt: m.reviewedAt, createdAt: m.createdAt },
          });
        }
      }

      // Developer-provided attributes: <img data-aimarker="ai-generated" data-aimarker-tool="Midjourney">
      document.querySelectorAll<HTMLElement>("[data-aimarker]").forEach((el) => {
        const origin = MANUAL[(el.dataset.aimarker || "").toLowerCase()];
        if (!origin || found.has(el)) return;
        found.set(el, {
          el,
          label: {
            kind: el instanceof HTMLImageElement || (!el.matches(TEXT_SELECTOR) && bgUrl(el)) ? "IMAGE" : "TEXT",
            origin,
            tool: el.dataset.aimarkerTool || null,
            reviewedBy: el.dataset.aimarkerReviewedBy || null,
            reviewedAt: null,
            createdAt: el.dataset.aimarkerDate || null,
          },
        });
      });

      const ctx = () => ({
        lang: app.lang,
        agency: policy.agencyName,
        siteName: site?.name,
        transparencyUrl: policy.showTransparencyLink ? site?.transparencyUrl : undefined,
        eu: policy.labelStyle !== "aimarker",
        explainer: policy.explainer,
      });

      if (article) {
        if (articleShown || (app.editing && article.mark)) {
          article.tag = textTag(policy, app.lang, article.label.origin, (tag) => layer.showCard(tag, article!.label, ctx()), true, !!article.label.reviewedBy, article.el, article.mark?.pagePath === "*");
          article.el.before(article.tag);
          hugBlock(article.tag, article.el, false, 14);
        }
        app.decorations.push(article);
      }

      for (const d of found.values()) {
        d.el.setAttribute("data-aimarker-origin", d.label.origin.toLowerCase().replace("_", "-"));
        // An article-level label already discloses the text on this page: don't repeat it per paragraph
        const covered = articleShown && !app.editing && d.label.kind === "TEXT" && d.label.origin !== "HUMAN";
        if (covered || !visible(d.label.kind, d.label)) {
          app.decorations.push(d);
          continue;
        }
        if (d.label.kind === "IMAGE") {
          d.badge = imageBadge(app.lang, d.label.origin, (btn) => layer.showCard(btn, d.label, ctx()), policy);
          layer.view.append(d.badge);
        } else {
          d.tag = textTag(policy, app.lang, d.label.origin, (tag) => layer.showCard(tag, d.label, ctx()), false, !!d.label.reviewedBy, d.el);
          // Own line above/below the block; list items and table cells keep it inside (a sibling there is invalid markup)
          const inside = /^(LI|TD|TH|DD)$/.test(d.el.tagName);
          if (policy.textPlacement === "end") inside ? d.el.append(d.tag) : d.el.after(d.tag);
          else inside ? d.el.prepend(d.tag) : d.el.before(d.tag);
          if (!inside) hugBlock(d.tag, d.el, policy.textPlacement === "end", 8);
        }
        app.decorations.push(d);
      }
      writeJsonLd();
      place();
    } finally {
      // Let our own DOM mutations flush before observing again
      setTimeout(() => (applying = false), 0);
    }
  }

  /** Pull the label line close to its block: cancel most of the block's own margin on that side (margins collapse). */
  function hugBlock(tag: HTMLElement, el: Element, below: boolean, gap = 6) {
    const cs = getComputedStyle(el);
    const m = parseFloat(below ? cs.marginBottom : cs.marginTop) || 0;
    if (m > gap + 2) tag.style.setProperty(below ? "margin-top" : "margin-bottom", `${-(m - gap)}px`, "important");
  }

  function place() {
    for (const d of app.decorations) if (d.badge) placeBadge(d.badge, d.el, policy.badgePosition);
  }

  function writeJsonLd() {
    const id = "aimarker-jsonld";
    document.getElementById(id)?.remove();
    const items = app.decorations
      .filter((d) => d.label.origin !== "HUMAN")
      .map((d) =>
        d.label.kind === "IMAGE"
          ? { "@type": "ImageObject", contentUrl: imageSrcOf(d.el), digitalSourceType: IPTC_SOURCE[d.label.origin] }
          : d.label.kind === "ARTICLE"
            ? { "@type": "Article", headline: textOf(d.el).trim().slice(0, 200), url: location.href, digitalSourceType: IPTC_SOURCE[d.label.origin] }
            : { "@type": "CreativeWork", text: textOf(d.el).trim().slice(0, 200), digitalSourceType: IPTC_SOURCE[d.label.origin] },
      );
    if (!items.length) return;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = id;
    s.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": items });
    document.head.appendChild(s);
  }

  // ── Reactivity: layout changes, lazy images, SPA navigation ────────────────
  let raf = 0;
  const schedulePlace = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(place);
  };
  window.addEventListener("resize", schedulePlace, { passive: true });
  window.addEventListener("scroll", schedulePlace, { passive: true, capture: true });
  document.addEventListener("load", schedulePlace, true);
  if ("ResizeObserver" in window) new ResizeObserver(schedulePlace).observe(document.documentElement);

  let timer = 0;
  const own = (n: Node) => n.nodeName.toLowerCase() === TAG_TAG || n.nodeName.toLowerCase() === HOST_TAG || n.nodeName === "SCRIPT";
  new MutationObserver((records) => {
    if (applying) return;
    const relevant = records.some((r) =>
      r.type === "attributes"
        ? !own(r.target) && (r.target as Element).closest?.(HOST_TAG) == null
        : [...Array.from(r.addedNodes), ...Array.from(r.removedNodes)].some((n) => !own(n)),
    );
    if (!relevant) return;
    clearTimeout(timer);
    timer = window.setTimeout(apply, 250);
  }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ["src", "srcset", "style", "class", "data-bg"] });

  let lastPath = app.pagePath();
  const onNav = () => {
    if (app.pagePath() === lastPath) return;
    lastPath = app.pagePath();
    layer.closeCard();
    void load();
  };
  for (const fn of ["pushState", "replaceState"] as const) {
    const orig = history[fn];
    history[fn] = function (this: History, ...args: Parameters<History["pushState"]>) {
      const r = orig.apply(this, args);
      setTimeout(onNav, 0);
      return r;
    };
  }
  window.addEventListener("popstate", onNav);

  w.AIMarker = { version: __VERSION__, refresh: load, get marks() { return app.marks; } };

  const start = async () => {
    await load();
    if (siteId) new Editor(app).maybeStart();
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => void start());
  else void start();
}

boot();
